// ============================================================
// xFreeScan - Login Page with OTP
// ============================================================
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { validateCorporateEmail } from '../../lib/emailValidation';
import { Shield, Mail, ArrowRight, AlertCircle, KeyRound, ArrowLeft, RefreshCw, Info } from 'lucide-react';

type Step = 'email' | 'otp';

export default function LoginPage() {
  const { requestOtp, verifyOtp, resendOtp, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  if (isAuthenticated) { navigate('/app'); return null; }

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (emailError) setEmailError('');
  };

  const handleEmailBlur = () => {
    if (email.trim()) {
      const err = validateCorporateEmail(email.trim());
      setEmailError(err || '');
    }
  };

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate corporate email before submitting
    const corpErr = validateCorporateEmail(email.trim());
    if (corpErr) {
      setEmailError(corpErr);
      return;
    }

    setIsLoading(true);
    try {
      await requestOtp(email.trim());
      setStep('otp');
      startResendCooldown();
    } catch (err: any) {
      setError(err.message || 'Error al enviar codigo');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const code = otpCode.join('');
    if (code.length !== 6) return;
    setError('');
    setIsLoading(true);
    try {
      await verifyOtp(email.trim(), code);
      navigate('/app');
    } catch (err: any) {
      setError(err.message || 'Codigo invalido');
      setOtpCode(['', '', '', '', '', '']);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      await resendOtp(email.trim());
      startResendCooldown();
      setError('');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const startResendCooldown = () => {
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleOtpInput = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otpCode];
    newOtp[index] = value.slice(-1);
    setOtpCode(newOtp);
    if (value && index < 5) document.getElementById(`otp-${index + 1}`)?.focus();
    if (newOtp.every(d => d !== '')) setTimeout(() => handleVerifyOtp(), 200);
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) document.getElementById(`otp-${index - 1}`)?.focus();
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtpCode(pasted.split(''));
      setTimeout(() => handleVerifyOtp(), 200);
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-purple-50 via-white to-orange-50">
      {/* Left panel */}
      <div className="hidden w-1/2 lg:flex lg:flex-col lg:justify-center lg:px-12 relative overflow-hidden bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900">
        <div className="absolute top-1/4 left-1/4 h-64 w-64 rounded-full bg-purple-400/20 blur-[100px]" />
        <div className="absolute bottom-1/4 right-1/4 h-48 w-48 rounded-full bg-orange-400/10 blur-[80px]" />

        <div className="relative z-10 max-w-lg">
          <div className="flex items-center gap-3 mb-10">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 backdrop-blur-sm">
              <Shield className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">xFreeScan</h1>
              <p className="text-xs text-purple-300">by XCloud Solutions</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-white">Acceso seguro con OTP</h2>
          <p className="mt-4 text-purple-200">
            Sin contrasenas. Cada vez que inicie sesion, le enviaremos un codigo unico a su correo electronico.
            Seguridad simple y efectiva.
          </p>
          <div className="mt-12 space-y-4">
            {['Sin contrasenas que recordar', 'Codigo unico cada sesion', 'Acceso exclusivo a sus escaneos'].map(text => (
              <div key={text} className="flex items-center gap-3">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-orange-400/20">
                  <ArrowRight className="h-3 w-3 text-orange-400" />
                </div>
                <span className="text-purple-100 text-sm">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex w-full flex-col justify-center px-4 sm:px-6 lg:w-1/2 lg:px-16">
        <div className="mx-auto w-full max-w-md">
          <Link to="/" className="mb-8 flex items-center gap-2 text-gray-500 hover:text-purple-700 transition-colors text-sm">
            <ArrowLeft className="h-4 w-4" /> Volver al inicio
          </Link>

          {step === 'email' ? (
            <>
              <h1 className="text-2xl font-bold text-gray-900">Iniciar Sesion</h1>
              <p className="mt-2 text-sm text-gray-500">
                No tiene cuenta?{' '}
                <Link to="/register" className="text-purple-600 font-medium hover:text-purple-500">Registrese gratis</Link>
              </p>

              {error && (
                <div className="mt-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" /> {error}
                </div>
              )}

              <form onSubmit={handleRequestOtp} className="mt-8 space-y-5">
                <div>
                  <label className="label">Correo Electronico Corporativo</label>
                  <div className="relative">
                    <Mail className={`absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 ${emailError ? 'text-red-400' : 'text-gray-400'}`} />
                    <input type="email" value={email}
                      onChange={e => handleEmailChange(e.target.value)}
                      onBlur={handleEmailBlur}
                      className={`input pl-11 ${emailError ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                      placeholder="nombre@suempresa.com" required autoFocus />
                  </div>
                  {emailError ? (
                    <div className="mt-1.5 flex items-start gap-1.5 rounded-lg bg-red-50 border border-red-200 px-3 py-2">
                      <AlertCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-red-600 font-medium">{emailError}</p>
                    </div>
                  ) : (
                    <div className="mt-1.5 flex items-start gap-1.5">
                      <Info className="h-3 w-3 text-gray-400 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-gray-400">Solo correos corporativos. No se aceptan Gmail, Hotmail, Yahoo, etc.</p>
                    </div>
                  )}
                </div>

                <button type="submit" disabled={isLoading} className="btn-primary w-full">
                  {isLoading ? (
                    <><div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" /> Enviando codigo...</>
                  ) : (
                    <>Enviar Codigo de Acceso <ArrowRight className="h-5 w-5" /></>
                  )}
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="text-center">
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-600 to-purple-700 text-white shadow-xl shadow-purple-500/20">
                  <KeyRound className="h-8 w-8" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Ingrese su codigo</h1>
                <p className="mt-2 text-sm text-gray-500">
                  Enviamos un codigo de 6 digitos a<br />
                  <span className="font-semibold text-purple-700">{email}</span>
                </p>
              </div>

              {error && (
                <div className="mt-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" /> {error}
                </div>
              )}

              <div className="mt-8 flex justify-center gap-3" onPaste={handleOtpPaste}>
                {otpCode.map((digit, i) => (
                  <input
                    key={i} id={`otp-${i}`} type="text" inputMode="numeric" maxLength={1} value={digit}
                    onChange={e => handleOtpInput(i, e.target.value)}
                    onKeyDown={e => handleOtpKeyDown(i, e)}
                    className="h-14 w-12 rounded-xl border-2 border-gray-300 bg-white text-center text-2xl font-bold text-purple-900 transition-all focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                    autoFocus={i === 0}
                  />
                ))}
              </div>

              <button onClick={handleVerifyOtp} disabled={isLoading || otpCode.join('').length !== 6} className="btn-primary w-full mt-8">
                {isLoading ? (
                  <><div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" /> Verificando...</>
                ) : (
                  <>Verificar y Acceder <ArrowRight className="h-5 w-5" /></>
                )}
              </button>

              <div className="mt-4 text-center">
                <button onClick={handleResend} disabled={resendCooldown > 0}
                  className="text-sm text-gray-500 hover:text-purple-600 disabled:opacity-50 inline-flex items-center gap-1">
                  <RefreshCw className="h-3.5 w-3.5" />
                  {resendCooldown > 0 ? `Reenviar en ${resendCooldown}s` : 'Reenviar codigo'}
                </button>
              </div>

              <button onClick={() => { setStep('email'); setError(''); }} className="mt-3 text-center text-sm text-gray-400 hover:text-gray-600 w-full">
                Usar otro correo
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
